import React, {useState, useEffect, useCallback} from 'react';
import InputWithLabel from "../Form/Input/InputWithLabel";
import TextareaWithLabel from "../Form/Input/TextareaWithLabel";
import WorkingHoursDisplay from "../Form/WorkingHoursDisplay"; // Импортируем новый компонент
import WorkingHoursEditor from "../Form/WorkingHoursEditor"; // Импортируем компонент редактора
import api from '../../api';
import ReactDOM from 'react-dom'; // Import ReactDOM for portals

const EditProfilePopup = ({show, closeEditProfilePopup, initialData, onSave, isAttorney = false}) => {
    const currentYear = new Date().getFullYear();

    // Инициализация состояния формы
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        foundation_year: '',
        phone_number: '',
        web_site: '',
        description: '',
        social_media: initialData?.social_media || [],
        additional_phones: initialData?.additional_phones || [],
        address: {
            country_id: '',
            state_id: '',
            city_id: '',
            zip_code_id: '',
            street_address: '',
        },
        free_consultation: initialData?.free_consultation || false,
        working_hours: initialData && !isAttorney && Array.isArray(initialData.working_hours)
            ? initialData.working_hours.map(wh => ({
                working_hours_id: wh.working_hours_id || wh.working_hours?.working_hours_id,
                day_of_week: wh.day_of_week || wh.working_hours?.day_of_week,
                start_time: wh.start_time || wh.working_hours?.start_time,
                end_time: wh.end_time || wh.working_hours?.end_time,
            }))
            : [],
    });

    // Добавляем состояние для ошибок
    const [errors, setErrors] = useState({});

    const [platforms, setPlatforms] = useState([]);
    const [contactMethods, setContactMethods] = useState([]);
    const [showAddSocialPopup, setShowAddSocialPopup] = useState(false);
    const [newSocialMedia, setNewSocialMedia] = useState({platform_id: '', url: ''});
    const [showAddPhonePopup, setShowAddPhonePopup] = useState(false);
    const [newPhone, setNewPhone] = useState({phone_type_id: '', phone_number: ''});
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);

    // Состояния для модального окна управления рабочими часами
    const [showEditWorkingHoursPopup, setShowEditWorkingHoursPopup] = useState(false);

    // Обновление formData при изменении workingHours
    useEffect(() => {
        setFormData((prevData) => ({
            ...prevData,
            working_hours: formData.working_hours || []
        }));
    }, [formData.working_hours]);

    // Функции для работы с адресом
    const handleAddressChange = async (name, value) => {
        let parsedValue = value;

        if (['state_id', 'city_id', 'zip_code_id', 'country_id'].includes(name)) {
            parsedValue = Number(value);
        }

        setFormData((prevData) => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: parsedValue
            }
        }));

        // Обработка зависимостей для адреса
        if (name === 'country_id') {
            fetchStates(parsedValue);
            setCities([]);
            setZipCodes([]);
        }

        if (name === 'state_id') {
            fetchCities(parsedValue);
            setZipCodes([]);
        }

        if (name === 'city_id') {
            fetchZipCodes(parsedValue);
        }
    };

    // Загрузка начальных данных
    useEffect(() => {
        if (initialData) {
            if (isAttorney) {
                setFormData((prevData) => ({
                    ...prevData,
                    first_name: initialData.first_name || '',
                    last_name: initialData.last_name || '',
                    email: initialData.email || '',
                    phone_number: initialData.phone_number || '',
                    description: initialData.description || '',
                    license_year: initialData.license_year || '',
                    total_won_amount: initialData.total_won_amount || '',
                    web_site: initialData.web_site || '',
                    social_media: initialData.social_media?.map(social => ({
                        social_media_id: social.social_media_id || '',
                        platform: social.platform || {},
                        url: social.url || ''
                    })) || [],
                    additional_phones: initialData.additional_phones || [],
                    address: {
                        country_id: initialData?.addresses?.[0]?.country?.country_id || 1,
                        state_id: Number(initialData?.addresses?.[0]?.state?.state_id) || '',
                        city_id: Number(initialData?.addresses?.[0]?.city?.city_id) || '',
                        zip_code_id: Number(initialData?.addresses?.[0]?.zip_code?.id) || '',
                        street_address: initialData?.addresses?.[0]?.street_address || '',
                    },
                    free_consultation: initialData?.free_consultation || false,
                }));
                if (initialData?.addresses?.[0]?.country?.country_id) {
                    fetchStates(Number(initialData.addresses[0].country.country_id));
                }
                if (initialData?.addresses?.[0]?.state?.state_id) {
                    fetchCities(Number(initialData.addresses[0].state.state_id));
                }
                if (initialData?.addresses?.[0]?.city?.city_id) {
                    fetchZipCodes(Number(initialData.addresses[0].city.city_id));
                }
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    name: initialData.name || '',
                    email: initialData.email || '',
                    foundation_year: initialData.foundation_year || '',
                    phone_number: initialData.phone_number || '',
                    web_site: initialData.web_site || '',
                    description: initialData.description || '',
                    social_media: initialData.social_media?.map(social => ({
                        social_media_id: social.social_media_id || '',
                        platform: social.platform || {},
                        url: social.url || ''
                    })) || [],
                    additional_phones: initialData.additional_phones || [],
                    address: {
                        country_id: initialData?.address?.country?.country_id || 1,
                        state_id: Number(initialData?.address?.state?.state_id) || '',
                        city_id: Number(initialData?.address?.city?.city_id) || '',
                        zip_code_id: Number(initialData?.address?.zip_code?.id) || '',
                        street_address: initialData?.address?.street_address || '',
                    },
                    free_consultation: initialData?.free_consultation || false,
                    working_hours: initialData.working_hours && Array.isArray(initialData.working_hours)
                        ? initialData.working_hours.map(wh => ({
                            working_hours_id: wh.working_hours_id || wh.working_hours?.working_hours_id,
                            day_of_week: wh.day_of_week || wh.working_hours?.day_of_week,
                            start_time: wh.start_time || wh.working_hours?.start_time,
                            end_time: wh.end_time || wh.working_hours?.end_time,
                        }))
                        : [],
                }));
                if (initialData?.address?.country?.country_id) {
                    fetchStates(Number(initialData.address.country.country_id));
                }
                if (initialData?.address?.state?.state_id) {
                    fetchCities(Number(initialData.address.state.state_id));
                }
                if (initialData?.address?.city?.city_id) {
                    fetchZipCodes(Number(initialData.address.city.city_id));
                }
            }
        }
    }, [initialData, isAttorney]);

    // Загрузка стран
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await api.get('/countries/');
                setCountries(response.data);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        fetchCountries();
    }, []);

    // Загрузка платформ
    useEffect(() => {
        const fetchPlatforms = async () => {
            try {
                const response = await api.get('/platforms/');
                setPlatforms(response.data);
            } catch (error) {
                console.error('Error fetching platforms:', error);
            }
        };

        fetchPlatforms();
    }, []);

    // Загрузка методов контактов
    useEffect(() => {
        const fetchContactMethods = async () => {
            try {
                const response = await api.get('/contact-methods/');
                setContactMethods(response.data);
            } catch (error) {
                console.error('Error fetching contact methods:', error);
            }
        };

        fetchContactMethods();
    }, []);

    // Загрузка штатов, городов, почтовых индексов
    const fetchStates = async (country_id) => {
        try {
            const response = await api.get(`/states-filtered/?country_id=${country_id}`);
            setStates(response.data);
        } catch (error) {
            console.error('Error fetching states:', error);
        }
    };

    const fetchCities = async (state_id) => {
        try {
            const response = await api.get(`/cities-filtered/?state_id=${state_id}`);
            setCities(response.data);
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    const fetchZipCodes = async (city_id) => {
        try {
            const response = await api.get(`/zip-codes/?city_id=${city_id}`);
            setZipCodes(response.data);
        } catch (error) {
            console.error('Error fetching zip codes:', error);
        }
    };

    // Обработка изменений в форме
    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    // Обработка изменений социальных сетей
    const handleSocialChange = (index, e) => {
        const {name, value} = e.target;
        const updatedSocialMedia = formData.social_media.map((social, i) => {
            if (i === index) {
                return {...social, [name]: value};
            }
            return social;
        });

        setFormData((prevData) => ({
            ...prevData,
            social_media: updatedSocialMedia
        }));
    };

    // Добавление новой социальной сети
    const handleAddSocialMedia = () => {
        const selectedPlatform = platforms.find(platform => platform.platform_id === Number(newSocialMedia.platform_id));

        if (selectedPlatform && newSocialMedia.url) {
            const updatedSocialMedia = [
                ...formData.social_media,
                {
                    platform: selectedPlatform,
                    url: newSocialMedia.url,
                    social_media_id: newSocialMedia.social_media_id || null
                }
            ];

            setFormData((prevData) => ({
                ...prevData,
                social_media: updatedSocialMedia
            }));

            setNewSocialMedia({platform_id: '', url: '', social_media_id: null});
            setShowAddSocialPopup(false);
        } else {
        }
    };

    // Удаление социальной сети
    const handleDeleteSocialMedia = (index) => {
        const updatedSocialMedia = formData.social_media.filter((_, i) => i !== index);

        setFormData((prevData) => ({
            ...prevData,
            social_media: updatedSocialMedia
        }));
    };

    // Обработка изменений дополнительных телефонов
    const handlePhoneChange = (index, e) => {
        const {name, value} = e.target;
        const updatedPhones = formData.additional_phones.map((phone, i) => {
            if (i === index) {
                return {...phone, [name]: value};
            }
            return phone;
        });

        setFormData((prevData) => ({
            ...prevData,
            additional_phones: updatedPhones
        }));
    };

    // Добавление нового телефона
    const handleAddPhone = () => {
        if (newPhone.phone_type_id && newPhone.phone_number) {
            const selectedPhoneType = contactMethods.find(method => method.value[1] === Number(newPhone.phone_type_id));

            const updatedPhones = [
                ...formData.additional_phones,
                {
                    phone_type_id: Number(newPhone.phone_type_id),
                    phone_type: selectedPhoneType.name,
                    phone_number: newPhone.phone_number
                }
            ];

            setFormData((prevData) => ({
                ...prevData,
                additional_phones: updatedPhones
            }));

            setNewPhone({phone_type_id: '', phone_number: ''});
            setShowAddPhonePopup(false);
        }
    };

    // Удаление телефона
    const handleDeletePhone = (index) => {
        const updatedPhones = formData.additional_phones.filter((_, i) => i !== index);

        setFormData((prevData) => ({
            ...prevData,
            additional_phones: updatedPhones
        }));
    };

    // Валидация данных формы
    const validateForm = useCallback(() => {
        const newErrors = {};

        if (isAttorney) {
            if (!formData.first_name.trim()) {
                newErrors.first_name = 'First name is required';
            }

            if (!formData.last_name.trim()) {
                newErrors.last_name = 'Last name is required';
            }

            if (!formData.license_year) {
                newErrors.license_year = 'License year is required';
            } else if (formData.license_year < 1900 || formData.license_year > currentYear) {
                newErrors.license_year = `License year must be between 1900 and ${currentYear}`;
            }

            if (!formData.total_won_amount) {
                newErrors.total_won_amount = 'Total won amount is required';
            } else if (formData.total_won_amount < 0) {
                newErrors.total_won_amount = 'Total won amount cannot be negative';
            }
        } else {
            if (!formData.name.trim()) {
                newErrors.name = 'Name is required';
            }

            if (!formData.foundation_year) {
                newErrors.foundation_year = 'Foundation year is required';
            } else if (formData.foundation_year < 1800 || formData.foundation_year > currentYear) {
                newErrors.foundation_year = `Foundation year must be between 1800 and ${currentYear}`;
            }
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else {
            // Простая валидация формата email
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Invalid email format.';
            }
        }

        if (!formData.phone_number.trim()) {
            newErrors.phone_number = 'Phone number is required';
        } else {
            // Валидация формата номера телефона
            const phoneRegex = /^\+?[0-9]{7,15}$/;
            if (!phoneRegex.test(formData.phone_number)) {
                newErrors.phone_number = 'Invalid phone number format.';
            }
        }

        if (formData.web_site) {
            let website = formData.web_site.trim();

            // Автоматически добавляем 'https://' если отсутствует 'http://' или 'https://'
            if (!/^https?:\/\//i.test(website)) {
                website = `https://${website}`;
                formData.web_site = website; // Обновляем URL в formData
            }

            // Проверка наличия точки в веб-сайте
            const domainContainsDotRegex = /\..+$/;
            if (!domainContainsDotRegex.test(website)) {
                newErrors.web_site = 'Веб-сайт должен содержать точку, за которой следует домен, например ".com".';
            }

            // Дополнительная проверка формата URL (опционально)
            const urlRegex = /^(https?:\/\/)([\w-]+(\.[\w-]+)+)(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
            if (!urlRegex.test(website)) {
                newErrors.web_site = 'Неверный формат URL веб-сайта.';
            }
        }


        if (formData.description.trim().length < 300) {
            newErrors.description = 'Description must be at least 300 characters';
        }

        // Валидация адреса
        if (!formData.address.country_id) {
            newErrors.address = {...newErrors.address, country_id: 'Country is required'};
        }
        if (!formData.address.state_id) {
            newErrors.address = {...newErrors.address, state_id: 'State is required'};
        }
        if (!formData.address.city_id) {
            newErrors.address = {...newErrors.address, city_id: 'City is required'};
        }
        if (!formData.address.zip_code_id) {
            newErrors.address = {...newErrors.address, zip_code_id: 'Zip Code is required'};
        }
        if (!formData.address.street_address.trim()) {
            newErrors.address = {...newErrors.address, street_address: 'Street and House Number is required'};
        }

        // Валидация социальных сетей (опционально)
        formData.social_media.forEach((social, index) => {
            let url = social.url.trim();

            // Автоматически добавляем 'https://' если отсутствует 'http://' или 'https://'
            if (!/^https?:\/\//i.test(url)) {
                url = `https://${url}`;
                formData.social_media[index].url = url; // Обновляем URL в formData
            }

            if (!url) {
                newErrors.social_media = newErrors.social_media || {};
                newErrors.social_media[index] = {url: 'URL is required'}; // Сообщение на английском
            } else {
                // Используем более строгий регулярное выражение для проверки URL
                const urlRegex = /^(https?:\/\/)([\w-]+(\.[\w-]+)+)(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
                if (!urlRegex.test(url)) {
                    newErrors.social_media = newErrors.social_media || {};
                    newErrors.social_media[index] = {url: 'Invalid URL format.'}; // Сообщение на английском
                }
            }
        });


        // // Валидация дополнительных телефонов (опционально)
        // formData.additional_phones.forEach((phone, index) => {
        //     if (!phone.phone_number.trim()) {
        //         newErrors.additional_phones = newErrors.additional_phones || {};
        //         newErrors.additional_phones[index] = { phone_number: 'Phone number is required' };
        //     } else {
        //         const phoneRegex = /^\+?[0-9]{7,15}$/;
        //         if (!phoneRegex.test(phone.phone_number)) {
        //             newErrors.additional_phones = newErrors.additional_phones || {};
        //             newErrors.additional_phones[index] = { ...newErrors.additional_phones[index], phone_number: 'Invalid phone number format.' };
        //         }
        //     }
        //     if (!phone.phone_type_id) {
        //         newErrors.additional_phones = newErrors.additional_phones || {};
        //         newErrors.additional_phones[index] = { ...newErrors.additional_phones[index], phone_type_id: 'Phone type is required' };
        //     }
        // });

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
        }

        return Object.keys(newErrors).length === 0;
    }, [formData, isAttorney, currentYear]);

    // Обработка сабмита формы
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const submissionData = {
            ...formData,
            working_hours: formData.working_hours.map((wh) => ({
                working_hours_id: wh.working_hours_id || null,
                day_of_week: wh.day_of_week,
                start_time: wh.start_time,
                end_time: wh.end_time,
            })),
        };
        onSave(submissionData);
    };

    // Отключение отправки формы при нажатии Enter
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    if (!show) return null;

    return ReactDOM.createPortal(
        <div className="modal visible" id="edit-profile">
            <div className="modal__overlay" onClick={closeEditProfilePopup}></div>
            <div className="modal__content">
                <button onClick={closeEditProfilePopup} className="button no-style close js--close-modal">
                    <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                </button>
                <div className="modal__form">
                    <form className="content" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                        <h2>Edit {isAttorney ? "Attorney" : "Company"} Profile</h2>

                        {/* Общие ошибки */}
                        {errors.general && (
                            <div className="error-message">
                                {errors.general}
                            </div>
                        )}

                        {/* Имя и фамилия для адвоката */}
                        {isAttorney && (
                            <>
                                <InputWithLabel
                                    label='First Name'
                                    name='first_name'
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    error={errors.first_name}
                                />
                                <InputWithLabel
                                    label='Last Name'
                                    name='last_name'
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    error={errors.last_name}
                                />
                                <InputWithLabel
                                    label='License Year'
                                    name='license_year'
                                    type='number'
                                    value={formData.license_year}
                                    onChange={handleChange}
                                    min="1900"
                                    max={currentYear}
                                    error={errors.license_year}
                                />
                                <InputWithLabel
                                    label='Total Won Amount'
                                    name='total_won_amount'
                                    type='number'
                                    value={formData.total_won_amount}
                                    onChange={handleChange}
                                    error={errors.total_won_amount}
                                />
                            </>
                        )}

                        {/* Имя компании и год основания */}
                        {!isAttorney && (
                            <>
                                <InputWithLabel
                                    label='Name'
                                    name='name'
                                    value={formData.name}
                                    onChange={handleChange}
                                    error={errors.name}
                                />
                                <InputWithLabel
                                    label='Foundation Year'
                                    name='foundation_year'
                                    type='number'
                                    value={formData.foundation_year}
                                    onChange={handleChange}
                                    min="1800"
                                    max={currentYear}
                                    error={errors.foundation_year}
                                />
                            </>
                        )}

                        {/* Электронная почта */}
                        <InputWithLabel
                            label='Email'
                            name='email'
                            type='email'
                            value={formData.email}
                            onChange={handleChange}
                            error={errors.email}
                        />

                        {/* Номер телефона */}
                        <InputWithLabel
                            label='Phone Number'
                            name='phone_number'
                            type='tel'
                            value={formData.phone_number}
                            onChange={handleChange}
                            error={errors.phone_number}
                        />

                        {/* Веб-сайт */}
                        <InputWithLabel
                            label='Website'
                            name='web_site'
                            type='url'
                            value={formData.web_site}
                            onChange={handleChange}
                            error={errors.web_site}
                        />

                        {/* Описание */}
                        <TextareaWithLabel
                            label='Description'
                            name='description'
                            value={formData.description}
                            onChange={handleChange}
                            error={errors.description}
                        />

                        {/* Отображаем поле "Free Consultation" только если service_type отсутствует */}
                        {!initialData?.service_type && (
                            <div className='free-consultation-toggle'>
                                <span>Free Consultation</span>
                                <label
                                    onClick={() => setFormData((prevData) => ({
                                        ...prevData,
                                        free_consultation: !prevData.free_consultation,
                                    }))}
                                    className={formData.free_consultation ? 'toggle checked' : 'toggle'}
                                >
                                    <div className='toggle__container'>
                                        <div className='toggle__circle'></div>
                                    </div>
                                </label>
                            </div>
                        )}

                        <span>Address</span>
                        {/* Поля для адреса */}
                        <select
                            className={`input ${errors.address?.country_id ? 'input-error' : ''}`}
                            value={Number(formData.address.country_id)}
                            onChange={(e) => handleAddressChange('country_id', e.target.value)}
                        >
                            <option value="" disabled>Choose country</option>
                            {countries.map((country) => (
                                <option key={country.country_id} value={Number(country.country_id)}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                        {errors.address?.country_id &&
                            <span className="error-message">{errors.address.country_id}</span>}

                        {states.length > 0 && (
                            <select
                                className={`input ${errors.address?.state_id ? 'input-error' : ''}`}
                                value={Number(formData.address.state_id)}
                                onChange={(e) => handleAddressChange('state_id', e.target.value)}
                            >
                                <option value="" disabled>Choose state</option>
                                {states.map((state) => (
                                    <option key={Number(state.state_id)} value={Number(state.state_id)}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {errors.address?.state_id && <span className="error-message">{errors.address.state_id}</span>}

                        {cities.length > 0 && (
                            <select
                                className={`input ${errors.address?.city_id ? 'input-error' : ''}`}
                                value={Number(formData.address.city_id)}
                                onChange={(e) => handleAddressChange('city_id', e.target.value)}
                            >
                                <option value="" disabled>Choose city</option>
                                {cities.map((city) => (
                                    <option key={Number(city.city_id)} value={Number(city.city_id)}>
                                        {city.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {errors.address?.city_id && <span className="error-message">{errors.address.city_id}</span>}

                        {zipCodes.length > 0 && (
                            <select
                                className={`input ${errors.address?.zip_code_id ? 'input-error' : ''}`}
                                value={Number(formData.address.zip_code_id) || ''}
                                onChange={(e) => handleAddressChange('zip_code_id', e.target.value)}
                            >
                                <option value="" disabled>Choose zip code</option>
                                {zipCodes.map((zip) => (
                                    <option key={zip.id} value={zip.id}>
                                        {zip.zip_code}
                                    </option>
                                ))}
                            </select>
                        )}
                        {zipCodes.length > 0 && errors.address?.zip_code_id &&
                            <span className="error-message">{errors.address.zip_code_id}</span>}

                        <InputWithLabel
                            label='Street Address'
                            name='street_address'
                            value={formData.address.street_address}
                            onChange={(e) => handleAddressChange('street_address', e.target.value)}
                            error={errors.address?.street_address}
                        />

                        {/* Социальные сети */}
                        <span>Social Networks</span>
                        {formData.social_media.map((social, index) => (
                            <InputWithLabel
                                key={index}
                                label={social.platform.name}
                                name="url"
                                value={social.url}
                                onChange={(e) => handleSocialChange(index, e)}
                                showDelete={true}
                                onDelete={() => handleDeleteSocialMedia(index)}
                                error={errors.social_media?.[index]?.url}
                            />
                        ))}
                        <button
                            className="underline no-style"
                            type="button"
                            onClick={() => setShowAddSocialPopup(true)}
                        >
                            Add Other Network
                        </button>

                        {showAddSocialPopup && (
                            <div className="modal visible" id="add-social">
                                <div className="modal__overlay" onClick={() => setShowAddSocialPopup(false)}></div>
                                <div className="modal__content">
                                    <button
                                        className="button no-style close js--close-modal"
                                        onClick={() => setShowAddSocialPopup(false)}
                                    >
                                        <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                                    </button>
                                    <div className="modal__form">
                                        <form className="content" onSubmit={(e) => {
                                            e.preventDefault();
                                            handleAddSocialMedia();
                                        }}>
                                            <h2>Add Social Network</h2>
                                            <select
                                                className={`input ${errors.newSocialMedia?.platform_id ? 'input-error' : ''}`}
                                                value={newSocialMedia.platform_id}
                                                onChange={(e) => setNewSocialMedia({
                                                    ...newSocialMedia,
                                                    platform_id: e.target.value
                                                })}
                                            >
                                                <option value="" disabled>Choose platform</option>
                                                {platforms.map((platform) => (
                                                    <option key={platform.platform_id} value={platform.platform_id}>
                                                        {platform.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.newSocialMedia?.platform_id && <span
                                                className="error-message">{errors.newSocialMedia.platform_id}</span>}

                                            <InputWithLabel
                                                label='Add link'
                                                name='url'
                                                value={newSocialMedia.url}
                                                onChange={(e) => setNewSocialMedia({
                                                    ...newSocialMedia,
                                                    url: e.target.value
                                                })}
                                                error={errors.newSocialMedia?.url}
                                            />

                                            <button className="button button-black" type="button"
                                                    onClick={handleAddSocialMedia}>
                                                Add Profile
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Дополнительные телефоны */}
                        <span>Additional Phones</span>
                        {formData.additional_phones.map((phone, index) => (
                            <InputWithLabel
                                key={index}
                                label={phone.phone_type.name}
                                name="phone_number"
                                value={phone.phone_number}
                                onChange={(e) => handlePhoneChange(index, e)}
                                showDelete={true}
                                onDelete={() => handleDeletePhone(index)}
                                error={errors.additional_phones?.[index]?.phone_number}
                            />
                        ))}
                        <button
                            className="underline no-style"
                            type="button"
                            onClick={() => setShowAddPhonePopup(true)}
                        >
                            Add Another Phone
                        </button>

                        {showAddPhonePopup && (
                            <div className="modal visible" id="add-phone">
                                <div className="modal__overlay" onClick={() => setShowAddPhonePopup(false)}></div>
                                <div className="modal__content">
                                    <button
                                        className="button no-style close js--close-modal"
                                        onClick={() => setShowAddPhonePopup(false)}
                                    >
                                        <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                                    </button>
                                    <div className="modal__form">
                                        <form className="content" onSubmit={(e) => {
                                            e.preventDefault();
                                            handleAddPhone();
                                        }}>
                                            <h2>Add Phone</h2>
                                            <select
                                                className={`input ${errors.newPhone?.phone_type_id ? 'input-error' : ''}`}
                                                value={newPhone.phone_type_id}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setNewPhone({
                                                        ...newPhone,
                                                        phone_type_id: newValue
                                                    });
                                                }}
                                            >
                                                <option value="" disabled>Choose phone type</option>
                                                {contactMethods
                                                    .filter(method => Array.isArray(method.value))
                                                    .map((method) => (
                                                        <option key={method.value[1]} value={method.value[1]}>
                                                            {method.name}
                                                        </option>
                                                    ))}
                                            </select>
                                            {errors.newPhone?.phone_type_id &&
                                                <span className="error-message">{errors.newPhone.phone_type_id}</span>}

                                            <InputWithLabel
                                                label='Phone Number'
                                                name='phone_number'
                                                value={newPhone.phone_number}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setNewPhone({
                                                        ...newPhone,
                                                        phone_number: newValue
                                                    });
                                                }}
                                                error={errors.newPhone?.phone_number}
                                            />

                                            <button className="button button-black" type="button" onClick={() => {
                                                handleAddPhone();
                                            }}>
                                                Add Phone
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Работа с рабочими часами */}
                        {!isAttorney && (
                            <>
                                <WorkingHoursDisplay workingHours={formData.working_hours}/>
                                <button
                                    type="button"
                                    className="button button-border"
                                    onClick={() => setShowEditWorkingHoursPopup(true)}
                                >
                                    Edit Working Hours
                                </button>
                            </>
                        )}

                        {/* Добавление модального окна для редактирования рабочих часов */}
                        {showEditWorkingHoursPopup && (
                            <WorkingHoursEditor
                                workingHours={formData.working_hours}
                                setWorkingHours={(updatedWorkingHours) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        working_hours: updatedWorkingHours,
                                    }));
                                }}
                                onClose={() => setShowEditWorkingHoursPopup(false)}
                            />
                        )}

                        {/* Кнопка отправки формы */}
                        <button className="button button-black" type="submit">Save Profile</button>
                    </form>
                </div>
            </div>
        </div>,
        document.body // Портал в body
    );
};

export default EditProfilePopup;
